import React, { useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import CallToAction from '../components/CallToAction';

const AboutPage = () => {
  useEffect(() => {
    document.title = "About Snap Launch Studio | Our Team & Approach to Website Development";
  }, []);

  return (
    <>
      <PageHeader 
        title="About Snap Launch Studios" 
      />
      
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto mb-16">
            <div className="prose prose-lg">
              <p className="text-lg">
                We started Snap Launch Studios after building and growing our own business online and realizing how many small business websites were quietly holding people back.
              </p>
              <p className="text-lg">
                Too often, websites are slow, outdated, hard to navigate, and even harder to update. They aren't doing the one thing they should: helping you bring in new business.
              </p>
              <p className="text-lg">
                So we took a different approach.
              </p>
              <p className="text-lg">
                Instead of offering custom services or dragging you through long proposals, we design and build better versions of small business websites upfront. Then we show them to you. If you like what you see, we'll get it live fast and hand everything over. If not, no pressure and no hard sell.
              </p>
              <p className="text-lg">
                We focus on contractors and service-based businesses like plumbers, HVAC pros, electricians, landscapers, and roofers. We know your time is limited, your margins matter, and your website should be working as hard as you are.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Every Snap Launch site is:</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="glass-card p-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-8 h-8 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-lg font-medium">Fast, mobile-friendly, and easy to manage</p>
              </div>
              
              <div className="glass-card p-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-8 h-8 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className="text-lg font-medium">Designed to generate leads and convert customers</p>
              </div>
              
              <div className="glass-card p-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-8 h-8 text-primary">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-lg font-medium">Built in advance so you know exactly what you're getting</p>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-xl font-semibold text-primary">
                We're not here to sell you a service. We're here to give you a website that actually helps your business grow.
              </p>
            </div>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">Meet the Team</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="glass-card overflow-hidden">
                <div className="h-64 bg-neutral-light flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://storage.googleapis.com/msgsndr/RMxB1SgTctXFwLszDoIk/media/684352c40baa555082bf70e9.jpeg" 
                    alt="AJ Bellamah - Founder & Developer" 
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 20%" }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold">AJ Bellamah</h3>
                  <p className="text-primary font-medium mb-4">Founder & Developer</p>
                  <p className="text-secondary-light">
                    AJ is a developer and small business owner who launched Snap Launch Studios with a simple goal: help other businesses grow by giving them websites that actually work. With a strong background in design, development, and local entrepreneurship, AJ focuses on building fast, functional websites that turn visitors into customers. He's the one behind the demos — always looking for ways to make a business's first impression online a great one.
                  </p>
                </div>
              </div>
              
              <div className="glass-card overflow-hidden">
                <div className="h-64 bg-neutral-light flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://storage.googleapis.com/msgsndr/RMxB1SgTctXFwLszDoIk/media/6844764e17e1e646d2b24ca7.png" 
                    alt="Max S - Product & Strategy" 
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "center 15%" }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold">Max S</h3>
                  <p className="text-primary font-medium mb-4">Product & Strategy</p>
                  <p className="text-secondary-light">
                    Max is a business leader with 8+ years of experience helping small businesses get more out of technology. From websites and apps to back-end tools, he's led product work that helps companies attract new customers, streamline operations, and grow sustainably. At Snap Launch Studios, Max focuses on strategy, user experience, and making sure every project delivers real business value — not just nice design.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <CallToAction title="Ready to see your new website?" />
      </>
  );
};

export default AboutPage;
